import Carousel from "nuka-carousel";
import { useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import Link from "@Components/Link";
import Icon from "@Components/ui/icon/Icon";
import { CMSImageType } from "@Types/Contentful";
import { usePreviousWF } from "@wff/hooks/useMemoize";
import ResponsiveImage from "./ResponsiveImage";

import classes from "./ImageSlider.module.scss";

interface IImageSliderProps {
  lang?: string;
  slug?: string;
  title?: string;
  isOnStock?: boolean;
  primaryImage: CMSImageType;
  secondaryImage?: CMSImageType;
}

// By business requirements in desktop view by default should present the primary image
// If image has a hover should present the secondary image
const PRIMARY_IMAGE_INDEX = 0;
const SECONDARY_IMAGE_INDEX = 1;

export default function ImageSlider({
  lang,
  slug,
  title,
  isOnStock,
  primaryImage,
  secondaryImage,
}: IImageSliderProps) {
  const isDesktop = useMediaQuery("(min-width:60em)");
  const prevIsDesktop = usePreviousWF(isDesktop);
  const [slideIndex, setSlideIndex] = useState<number>(PRIMARY_IMAGE_INDEX);

  useEffect(() => {
    // Need to compare previous with current media queries state
    // to avoid setting slide index each time when changed slideIndex in the desktop view
    if (prevIsDesktop !== isDesktop && slideIndex !== PRIMARY_IMAGE_INDEX) {
      setSlideIndex(PRIMARY_IMAGE_INDEX);
    }
  }, [isDesktop, prevIsDesktop, slideIndex]);

  const settings = {
    swiping: !isDesktop,
    slideIndex,
    dragging: !isDesktop,
    wrapAround: !isDesktop,
    withoutControls: isDesktop,
    renderBottomCenterControls: null,
    defaultControlsConfig: {
      prevButtonClassName: classes.arrow,
      prevButtonText: <Icon iconName={`arrow-left-thin`} />,
      nextButtonClassName: classes.arrow,
      nextButtonText: <Icon iconName={`arrow-right-thin`} />,
    },
    beforeSlide: (_currentSlideIndex: number, nextArrowIndex: number) =>
      setSlideIndex(nextArrowIndex),
  };

  if (!secondaryImage) {
    return (
      <Link aria-label={title} href={`/${lang}/products/${slug}`}>
        <div className={classes.slider}>
          <ResponsiveImage
            isOnStock={isOnStock}
            alt={primaryImage?.title ?? "primary-image"}
            src={`${primaryImage?.url?.split("?")?.[0]}?fm=webp&w=317&h=317`}
          />
        </div>
      </Link>
    );
  }

  return (
    <Link aria-label={title} href={`/${lang}/products/${slug}`}>
      <div
        className={classes.slider}
        onMouseOver={() => isDesktop && setSlideIndex(SECONDARY_IMAGE_INDEX)}
        onMouseLeave={() => isDesktop && setSlideIndex(PRIMARY_IMAGE_INDEX)}
      >
        <Carousel {...settings}>
          <ResponsiveImage
            isOnStock={isOnStock}
            alt={primaryImage?.title ?? "primary-image"}
            src={`${primaryImage?.url?.split("?")?.[0]}?fm=webp&w=317&h=317`}
          />

          <ResponsiveImage
            isOnStock={isOnStock}
            alt={secondaryImage?.title ?? "secondary-image"}
            src={`${secondaryImage?.url?.split("?")?.[0]}?fm=webp&w=317&h=317`}
          />
        </Carousel>
      </div>
    </Link>
  );
}
